/**
 * Library Card Component
 * Displays core content pillars with better affordances
 */

'use client';

import { memo } from 'react';
import Link from 'next/link';
import { Card } from '@/components/ui/card';
import { BookOpen, ChevronRight } from 'lucide-react';

export const LibraryCard = memo(function LibraryCard() {
  return (
    <Card
      className="p-4 sm:p-5 md:p-6 lg:p-7 hover:shadow-xl transition-all border-l-4 border-l-primary hover:border-l-primary/80 shadow-sm"
      role="region"
      aria-label="Learning resources"
    >
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-5 flex items-center gap-2 sm:gap-3">
        <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-primary" aria-hidden="true" />
        <span className="text-foreground">Learning Library</span>
      </h2>
      <nav aria-label="Learning resources" className="space-y-2 sm:space-y-2.5">
        <Link
          href="/modules"
          className="group flex items-center justify-between p-3.5 sm:p-4 rounded-xl hover:bg-primary/5 transition-all border border-border hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[60px] sm:min-h-[64px]"
          aria-label="Browse 11 learning modules"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 sm:gap-2.5 mb-1 sm:mb-1.5">
              <div className="font-semibold text-base sm:text-lg text-foreground group-hover:text-primary transition-colors">
                Learning Modules
              </div>
              <span
                className="text-xs font-semibold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                aria-label="11 modules available"
              >
                11 modules
              </span>
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-0.5 sm:mt-1">
              Comprehensive courses on knowledge, skills, virtues, and practice
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-2 sm:ml-3">
            <span className="text-xs sm:text-sm text-primary font-semibold transition-colors whitespace-nowrap">
              Explore
            </span>
            <ChevronRight
              className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1.5"
              aria-hidden="true"
            />
          </div>
        </Link>
        <Link
          href="/scenarios"
          className="group flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-all border border-transparent hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Browse practice scenarios"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2.5">
              <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                Practice Scenarios
              </div>
              <span className="text-xs text-muted-foreground font-medium" aria-label="3 scenarios">
                3 scenarios
              </span>
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed mt-0.5">
              Crisis, Socratic, Stakeholder
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <span className="text-xs text-muted-foreground group-hover:text-primary font-medium transition-colors whitespace-nowrap">
              Run
            </span>
            <ChevronRight
              className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </div>
        </Link>
        <Link
          href="/blog"
          className="group flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-all border border-transparent hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Read blog articles"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2.5">
              <div className="font-semibold text-foreground group-hover:text-primary transition-colors">
                Blog & Articles
              </div>
              <span className="text-xs text-muted-foreground font-medium" aria-label="12 articles">
                12 articles
              </span>
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed mt-0.5">
              Deep dives and guides
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <span className="text-xs text-muted-foreground group-hover:text-primary font-medium transition-colors whitespace-nowrap">
              Read
            </span>
            <ChevronRight
              className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </div>
        </Link>
      </nav>
    </Card>
  );
});
